import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

export interface TimeframeOption<T extends string> {
  value: T;
  label: string;
}

interface TimeframeMenuProps<T extends string> {
  value: T;
  options: TimeframeOption<T>[];
  onChange: (value: T) => void;
}

export function TimeframeMenu<T extends string>({ value, options, onChange }: TimeframeMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const current = options.find((o) => o.value === value)?.label ?? options[0]?.label ?? "";

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-100 transition-colors"
        title="View mode"
      >
        <span>{current}</span>
        <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-36 bg-background-elevated border border-border-default rounded-md shadow-lg z-50 py-1">
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                value === o.value
                  ? "bg-blue-50 text-blue-700 font-medium"
                  : "text-text-primary hover:bg-surface-hover"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
